import { useState } from 'react';
import { Camera, Check, Loader2, X } from 'lucide-react';

// Upload de UMA foto de evidência dos formulários de qualidade (Avaliação de
// Segurança do Alimento / Avaliação de Treinamento — ex-Auditoria SA/TSV).
// Sobe na hora (POST <api>/upload-foto → SharePoint) e devolve a URL pelo
// callback.
//
// POR QUE UPLOAD SEPARADO, e não base64 junto do envio: foto de celular são
// 3–5 MB. Uma visita com 10 negativas viraria um POST de dezenas de MB, que
// morre no client_max_body_size do Nginx ou na rede da loja.
//
// (Espelha o FotoUpload da Avaliação BSV e o da Manutenção; muda o endpoint e
// os metadados, que aqui batizam o arquivo no SharePoint. Unificar as três
// cópias está registrado no PLANO › Trilho A.)

// Redimensiona no NAVEGADOR antes de subir: lado maior ≤ 1280px, JPEG 0.7.
// Uma foto de 4 MB vira ~200 KB — o que torna o upload viável no 4G da loja.
const MAX_LADO = 1280;
const redimensionar = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > MAX_LADO) {
          height = Math.round((height * MAX_LADO) / width); width = MAX_LADO;
        } else if (height > MAX_LADO) {
          width = Math.round((width * MAX_LADO) / height); height = MAX_LADO;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        // Navegador sem toBlob: sobe o arquivo original.
        canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.7);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

/**
 * @param {string} api       Prefixo da API do formulário ('/api/avaliacao' | '/api/auditoria')
 * @param {string} url       URL já enviada (vazio = slot livre)
 * @param {func}   onUrl     Recebe a URL nova (ou '' ao remover)
 * @param {object} meta      { loja, data, pergunta, ordem, chave } — batizam o arquivo
 * @param {bool}   desabilitado  Sem loja/data ainda: o arquivo nasceria sem identificação
 */
const FotoEvidencia = ({ api, url, onUrl, meta, desabilitado }) => {
  const [estado, setEstado] = useState('idle'); // idle | enviando | erro
  const [erro, setErro] = useState('');

  const enviar = async (file) => {
    if (!file) return;
    setEstado('enviando'); setErro('');
    try {
      let envio = file;
      try { envio = await redimensionar(file); } catch { /* mantém o original */ }
      const fd = new FormData();
      fd.append('foto', envio, 'foto.jpg');
      // Metadados do nome do arquivo no SharePoint (DATA_MÓDULO_LOJA_PERGUNTA_nº).
      Object.entries(meta || {}).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const r = await fetch(`${api}/upload-foto`, { method: 'POST', headers: auth(), body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Falha no upload.');
      onUrl(data.url);
      setEstado('idle');
    } catch (e) {
      setEstado('erro');
      setErro(e.message || 'Falha no upload.');
    }
  };

  // Remover: limpa a URL no pai (volta a contar como "sem foto" na validação).
  const remover = () => { onUrl(''); setEstado('idle'); setErro(''); };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <label
        title={desabilitado ? 'Preencha a loja e a data no topo antes de anexar fotos' : undefined}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          cursor: desabilitado ? 'not-allowed' : 'pointer',
          padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700,
          border: '1px solid', borderColor: url ? '#c7d2fe' : '#cbd5e1',
          background: url ? '#eef2ff' : '#fff',
          color: url ? '#4338ca' : '#475569',
          opacity: desabilitado ? 0.5 : 1,
        }}
      >
        {estado === 'enviando'
          ? <Loader2 size={14} className="spin" />
          : url ? <Check size={14} /> : <Camera size={14} />}
        {url ? `Foto ${meta?.ordem || ''}`.trim() : (meta?.ordem > 1 ? 'Outra foto' : 'Anexar foto')}
        {/* Sem `capture`: o celular abre o seletor nativo com as DUAS opções
            (tirar na hora OU escolher da galeria) — a visita às vezes é
            fotografada antes e preenchida depois. */}
        <input
          type="file" accept="image/*" style={{ display: 'none' }} disabled={desabilitado}
          onChange={(e) => { enviar(e.target.files?.[0]); e.target.value = ''; }}
        />
      </label>

      {/* Remover fica FORA do <label>: dentro dele, o clique abriria o seletor. */}
      {url && estado !== 'enviando' && (
        <button
          type="button" onClick={remover} title="Remover foto"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 24, height: 24, padding: 0, cursor: 'pointer', borderRadius: 6,
            border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c',
          }}
        >
          <X size={14} />
        </button>
      )}

      {estado === 'erro' && (
        <span style={{ color: '#b91c1c', fontSize: 11 }}>{erro}</span>
      )}
    </span>
  );
};

export default FotoEvidencia;
